/**
 * Calendar subscription (ICS feed) in Stillingar: create a personal link once ("Opna í dagatali" =
 * webcal://, "Afrita", "Deila"), replace it with a new one (old one stops working) or stop it.
 * The server keeps only a hash, so an existing link can never be shown again.
 */
import { CalendarCheck, CalendarOff, CalendarPlus, CalendarSync, Copy, Info, Link2, RefreshCw, Share2, WifiOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { CalendarFeedLinkDTO } from '../../../shared/dto/stats';
import { useCalendarFeedStatus, useCreateCalendarFeed, useDeleteCalendarFeed } from '../../api/stats';
import { useToast } from '../../components/toast';
import { Button, ConfirmSheet, ErrorBox, Spinner } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useOnline } from '../../lib/useOnline';
import { errorText } from './errorText';
import { SettingsSection } from './parts';

type Confirm = 'rotate' | 'stop' | null;

function FeedLinkPanel({ link }: { link: CalendarFeedLinkDTO }) {
  const { t } = useI18n();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    boxRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [link.httpsUrl]);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.httpsUrl);
      toast.show(t('common.copied'), { tone: 'ok' });
    } catch {
      inputRef.current?.focus();
      inputRef.current?.select();
      toast.show(t('stats.feed.copy_failed'), { tone: 'error', ms: 5000 });
    }
  };

  const share = async () => {
    try {
      await navigator.share({ title: t('app.name'), url: link.httpsUrl });
    } catch (e) {
      if ((e as Error)?.name !== 'AbortError') void copy();
    }
  };

  return (
    <div ref={boxRef} className="flex flex-col gap-3 rounded-2xl border-2 border-[var(--accent)] bg-[var(--accent-soft)] p-4">
      <div className="flex items-center gap-2 font-semibold text-[var(--accent)]">
        <Link2 className="size-5" aria-hidden />
        {t('stats.feed.your_link')}
      </div>
      <input
        ref={inputRef}
        readOnly
        value={link.httpsUrl}
        aria-label={t('stats.feed.link_label')}
        onFocus={(e) => e.currentTarget.select()}
        className="w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3 py-3 font-mono text-[16px] text-[var(--ink)] select-all"
      />
      <a
        href={link.webcalUrl}
        className="inline-flex min-h-14 items-center justify-center gap-2.5 rounded-2xl bg-[var(--accent)] px-6 text-[19px] font-semibold text-[var(--accent-ink)] transition hover:brightness-110 active:brightness-95"
      >
        <CalendarPlus className="size-6 shrink-0" aria-hidden />
        {t('stats.feed.open')}
      </a>
      <div className="flex flex-wrap gap-3">
        <Button variant="secondary" icon={Copy} onClick={() => void copy()} className="flex-1">
          {t('common.copy')}
        </Button>
        {canShare ? (
          <Button variant="secondary" icon={Share2} onClick={() => void share()} className="flex-1">
            {t('common.share')}
          </Button>
        ) : null}
      </div>
      <p className="text-[15px] text-[var(--ink-2)]">{t('stats.feed.shown_once')}</p>
      <p className="text-[14px] text-[var(--muted)]">{t('stats.feed.google')}</p>
    </div>
  );
}

export function CalendarFeedSection() {
  const { t } = useI18n();
  const toast = useToast();
  const online = useOnline();
  const status = useCalendarFeedStatus();
  const create = useCreateCalendarFeed();
  const remove = useDeleteCalendarFeed();
  /** The link just created — only ever held in memory, gone when the screen closes. */
  const [link, setLink] = useState<CalendarFeedLinkDTO | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const busy = create.isPending || remove.isPending;
  const active = link !== null || status.data?.active === true;

  const makeLink = async () => {
    try {
      setLink(await create.mutateAsync());
    } catch (e) {
      toast.show(errorText(t, e), { tone: 'error' });
    }
    setConfirm(null);
  };

  const stop = async () => {
    try {
      await remove.mutateAsync();
      setLink(null);
      toast.show(t('stats.feed.stopped'), { tone: 'ok' });
    } catch (e) {
      toast.show(errorText(t, e), { tone: 'error' });
    }
    setConfirm(null);
  };

  let content;
  if (link) {
    content = <FeedLinkPanel link={link} />;
  } else if (status.isPending && status.fetchStatus === 'paused') {
    content = (
      <p className="flex items-center gap-2 text-[var(--muted)]">
        <WifiOff className="size-5" aria-hidden />
        {t('stats.feed.needs_online')}
      </p>
    );
  } else if (status.isPending) {
    content = <Spinner label={t('common.loading')} />;
  } else if (status.isError && !status.data) {
    content = (
      <ErrorBox onRetry={() => void status.refetch()} retryLabel={t('common.retry')}>
        {errorText(t, status.error)}
      </ErrorBox>
    );
  } else if (!active) {
    content = (
      <Button icon={CalendarPlus} size="lg" busy={create.isPending} disabled={!online || busy} onClick={() => void makeLink()} className="self-start">
        {t('stats.feed.create')}
      </Button>
    );
  } else {
    content = (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 font-semibold text-[var(--ok-text)]">
          <CalendarCheck className="size-5" aria-hidden />
          {t('stats.feed.active')}
        </div>
        <p className="text-[15px] text-[var(--ink-2)]">{t('stats.feed.active_hint')}</p>
      </div>
    );
  }

  return (
    <SettingsSection icon={CalendarSync} title={t('stats.feed.title')} id="settings-calendar-feed">
      <div className="flex flex-col gap-4 p-5">
        <p className="text-[17px]">{t('stats.feed.intro')}</p>
        {content}
        {active && status.data ? (
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button variant="secondary" icon={RefreshCw} block disabled={!online || busy} onClick={() => setConfirm('rotate')}>
              {t('stats.feed.rotate')}
            </Button>
            <Button variant="secondary" icon={CalendarOff} block disabled={!online || busy} className="text-[var(--danger-text)]" onClick={() => setConfirm('stop')}>
              {t('stats.feed.stop')}
            </Button>
          </div>
        ) : null}
        {!online && status.data ? <p className="text-[15px] text-[var(--muted)]">{t('stats.feed.needs_online')}</p> : null}
        <div className="flex gap-2 rounded-xl bg-[var(--surface-2)] p-3 text-[14px] text-[var(--ink-2)]">
          <Info className="mt-0.5 size-4.5 shrink-0 text-[var(--muted)]" aria-hidden />
          <div className="flex flex-col gap-1">
            <p>{t('stats.feed.refresh_note')}</p>
            <p>{t('stats.feed.privacy_note')}</p>
          </div>
        </div>
      </div>
      <ConfirmSheet
        open={confirm !== null}
        title={confirm === 'stop' ? t('stats.feed.stop_confirm') : t('stats.feed.rotate_confirm')}
        body={confirm === 'stop' ? t('stats.feed.stop_body') : t('stats.feed.rotate_body')}
        confirmLabel={confirm === 'stop' ? t('stats.feed.stop') : t('stats.feed.rotate')}
        cancelLabel={t('common.cancel')}
        danger={confirm === 'stop'}
        busy={busy}
        onConfirm={() => void (confirm === 'stop' ? stop() : makeLink())}
        onClose={() => !busy && setConfirm(null)}
      />
    </SettingsSection>
  );
}
